import React, { useRef } from 'react';

import axios, { Canceler } from 'axios';
import { toast } from 'react-toastify';
import Backend from '../../../utils/Backend';
import useCancel from '../../Hooks/CancelFetch';
import { formatPrice } from '../../../helper/vehicle_formatter';
import useMobile from '../../Hooks/Mobile';

import Select from 'react-select';

import IOption from '../../../interfaces/IOption';
import IGenericOption from '../../../interfaces/IGenericOption';
import IModalState from '../../../interfaces/IModalState';

import styles from './styles.module.scss'
import { getVehicleStyle, getVehicleStyleBoi, vehicleTheme } from '../../../helper/style';
import { useIsBoiWebsite } from "../../Context/WebsiteType";

interface IEVModal {
  makes: IOption[],
  update: () => void,
  hideModal: () => void,
  modalState: IModalState,
  setModalState: (modalState: IModalState) => void,
  scrollY: number
}


export default function EVModal({ makes, update, hideModal, modalState, setModalState, scrollY }: IEVModal) {
  const modalRef = useRef<HTMLDivElement>(null);
  const cancelModelsRef = useRef<Canceler>()
  const cancelOptionsRef = useRef<Canceler>()

  const tablet = useMobile();

  const isBoiWebsite = useIsBoiWebsite();

  const {
    make,
    model, models,
    variant, variants,
  } = modalState

  useCancel(cancelModelsRef.current)
  const selectMake = (make: IOption | null) => {
    if (make) {
      // update Make and clean up State
      const state = {
        make,
        model: null,
        models: [],
        variant: null,
        variants: []
      }
      
      setModalState(state)

      // fetch new Models based on selected Make
      const { promise, cancel } = Backend.getVehicles([make], undefined, true)

      cancelModelsRef.current = cancel

      promise.then(({data}) => {
        let models = []

        if (Object.keys(data?.content?.vehicles)?.length !== 0) {
          models = data.content.vehicles
        }
        
        setModalState({
          ...state,
          models
        })
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;

        toast.error("Failed to fetch new models")
      })
    }
  }

  useCancel(cancelOptionsRef.current)
  const selectModel = (model: IOption | null) => {
    if (make && model) {
      const state = {
        make,
        model: model,
        models: modalState.models,
        variant: null,
        variants: []
      }
      
      setModalState(state)

      // fetch new Variants based on selected Models
      const { promise, cancel } = Backend.getOptions(parseInt(`${make.value}`), parseInt(`${model.value}`))
      
      cancelOptionsRef.current = cancel;

      promise.then(({data}) => {
        if (data.status === 200 && data.content?.vehicles?.length > 0) {
          setModalState({
            ...state,
            variants: data.content.vehicles.map((variant: any) => {
              return {
                value: variant,
                label: `${variant.trim_level} - ${formatPrice(variant.price)}`,
              }
            })
          })
        }
        else {
          toast.error("No options were found")
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        
        toast.error("Failed to fetch new variants")
      })
    }
  }
  
  const selectVariant = (variant: IGenericOption | null) => {
    setModalState({
      ...modalState,
      variant
    })

    update()
    
    handleClickOutside()
  }
  
  const handleClickOutside = () => {
    if (modalRef?.current) {
      modalRef.current.classList.toggle(styles.modalDisappear);

      window.scrollTo(0, scrollY)

      setTimeout(() => {
        hideModal();
      }, 490);  
    } 
  }  

  return (
    <div className={styles.modalHolder} ref={modalRef}>
      <div className={`blurry-cover ${styles.cover}`} onClick={handleClickOutside}>&nbsp;</div>
      
      <div className={`${styles.modal} ${isBoiWebsite ? styles.boi : styles.nevo}`}>
        <div className={styles.selectHolder}>
          <p>Make</p>

          <div className={styles.selectorHolder}>
            <Select
              instanceId="1"
              name="make"
              placeholder="Make"
              value={make}
              options={makes}
              onChange={selectMake}
              styles={isBoiWebsite ? getVehicleStyleBoi(false, true) : getVehicleStyle(false, true)}
              theme={vehicleTheme}
            />
          </div>
        </div>
        

        <div className={styles.selectHolder}>
          <p>Model</p>

          <div className={styles.selectorHolder}>
            <Select
              instanceId="2"
              name="model"
              placeholder="Model"
              value={model}
              options={models}
              onChange={selectModel}
              styles={isBoiWebsite ? getVehicleStyleBoi(false, true) : getVehicleStyle(false, true)}
              theme={vehicleTheme}
            />
          </div>
        </div>
        
        
        <div className={styles.selectHolder}>
          <p>Variant</p>

          <div className={styles.selectorHolder}>
            <Select
              instanceId="3"
              name="variant"
              placeholder="Variant"
              value={variant}
              options={variants}
              onChange={selectVariant}
              styles={isBoiWebsite ? getVehicleStyleBoi(false, true) : getVehicleStyle(false, true)}
              theme={vehicleTheme}
              isSearchable={!tablet}
            />
          </div>
        </div>
      </div>
    </div>
  )
};